/**
 * FilterSelect - the F filter dropdown, single or multi. Multi mode renders
 * the public Markets recipe: checkbox menu items, an "All X / label / N X"
 * summary, notched shrink label, accent focus ring, hairline menu paper.
 *
 * options: [{ value, label, secondary? }]
 */

import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, OutlinedInput, Checkbox, ListItemText, Box } from '@mui/material';
import { useThemeMode } from '../../context/ThemeContext';
import { useRecipes } from '../../theme/recipes';

let uid = 0;

function FilterSelect({
  label,
  value,
  onChange,
  options = [],
  multiple = false,
  plural,            // e.g. "states" - used in the multi summary text
  fullWidth = true,
  minWidth,
  size = 'small',
}) {
  const { palette } = useThemeMode();
  const { controlSx, labelSx, menuProps, checkboxSx } = useRecipes();
  const idRef = React.useRef(`fsel-${(uid += 1)}`);
  const labelId = `${idRef.current}-label`;

  const labelFor = (v) => options.find((o) => o.value === v)?.label ?? String(v);

  const renderMulti = (sel) => {
    const empty = !sel || sel.length === 0;
    const noun = plural || `${label ? label.toLowerCase() : 'option'}s`;
    const text = empty ? `All ${noun}` : sel.length === 1 ? labelFor(sel[0]) : `${sel.length} ${noun}`;
    return (
      <Box component="span" sx={{ fontSize: '0.86rem', color: empty ? palette.textMuted : palette.text }}>
        {text}
      </Box>
    );
  };

  return (
    <FormControl fullWidth={fullWidth} size={size} sx={minWidth ? { minWidth } : undefined}>
      <InputLabel shrink={multiple || undefined} id={labelId} sx={labelSx}>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        multiple={multiple}
        displayEmpty={multiple || undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        input={<OutlinedInput notched={multiple || undefined} label={label} sx={controlSx} />}
        renderValue={multiple ? renderMulti : undefined}
        MenuProps={menuProps}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {multiple && (
              <Checkbox size="small" checked={value.indexOf(opt.value) > -1} sx={checkboxSx} />
            )}
            <ListItemText
              primary={opt.label}
              secondary={opt.secondary}
              primaryTypographyProps={{ fontSize: '0.86rem' }}
              secondaryTypographyProps={opt.secondary ? { fontSize: '0.72rem', color: palette.textMuted } : undefined}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default FilterSelect;
